/**
 * German storefront Organization JSON-LD (`diekaffeefreunde.de`). Legal HQ stays NL in `address` / `vatID`; edit `ORGANIZATION_DE_STATIC` for URLs, logo, and copy.
 * @see https://developers.google.com/search/docs/appearance/structured-data/organization
 */
import type { OrganizationReviewBuildInput } from '$lib/seo/schemas/organizationReviews';
import type { Page } from '$models/Page/Page';
import type { Site } from '$models/Site/Site';
import { jsonLdScriptTag } from '$lib/seo/jsonLd';
import {
	findReviewsBlockOnLandingPage,
	mapReviewsBlockToOrganizationInputs,
	mergeOrganizationReviewsIntoSchema
} from '$lib/seo/schemas/organizationReviews';

export type BuildOrganizationDeInput = OrganizationReviewBuildInput;

const ORGANIZATION_DE_STATIC: Record<string, unknown> = {
	'@context': 'https://schema.org',
	'@type': 'Organization',
	name: 'Die Kaffeefreunde',
	alternateName: 'DKF',
	url: 'https://diekaffeefreunde.de',
	logo: 'https://cdn.cms.thesubscriptioncompany.nl/wp-content/uploads/sites/4/2024/11/20164559/Die-Kaffefreunde-logo-zwart-transparent.png',
	description:
		'De Koffiejongens liefert 100 % biologischen Specialty Coffee als Bohnen und Kapseln, ganz ohne Geschmackskompromisse. Unsere nachhaltigen Kapseln bestehen aus Pflanzen und sind heimkompostierbar. Alle Pakete werden von Menschen mit Barrieren zum Arbeitsmarkt verpackt. Dank unseres bequemen Lieferservice genießt Du sorgenfrei Spitzenkaffee und unterstützt gleichzeitig fairen Handel.',
	foundingDate: '2024',
	email: 'info@diekaffeefreunde.de',
	vatID: 'DE360562584',
	contactPoint: {
		'@type': 'ContactPoint',
		contactType: 'customer service',
		email: 'info@diekaffeefreunde.de',
		telephone: '+4928213914018',
		availableLanguage: ['German', 'Dutch', 'English'],
		areaServed: 'DE'
	},
	sameAs: [
		'https://www.instagram.com/diekaffeefreunde.de/',
		'https://www.facebook.com/DieKaffeefreunde/',
		'https://www.linkedin.com/company/de-koffiejongens/'
	],
	address: {
		'@type': 'PostalAddress',
		streetAddress: 'Ringstraße 16',
		addressLocality: 'Kleve',
		postalCode: '47533',
		addressCountry: 'DE'
	},
	numberOfEmployees: {
		'@type': 'QuantitativeValue',
		minValue: 15,
		maxValue: 25
	}
};

export function buildOrganizationDeSchema(
	input: OrganizationReviewBuildInput
): Record<string, unknown> {
	const schema: Record<string, unknown> = { ...ORGANIZATION_DE_STATIC };
	mergeOrganizationReviewsIntoSchema(schema, input);
	return schema;
}

/** Must match WordPress site `name` and home slug for diekaffeefreunde.de. */
export const ORGANIZATION_DE_INJECT = {
	pathname: '/',
	pageSlug: 'home',
	siteName: 'Die Kaffeefreunde',
	language: 'de'
} as const;

export type OrganizationDeHeadContext = {
	url: Pick<URL, 'pathname' | 'origin'>;
	page: Page;
	site: Pick<Site, 'name' | 'locale'>;
};

export function organizationDeHeadScriptHtml(context: OrganizationDeHeadContext): string {
	const g = ORGANIZATION_DE_INJECT;
	if (context.url.pathname !== g.pathname) {
		return '';
	}
	if (
		context.site.locale.language !== g.language ||
		context.site.name !== g.siteName ||
		context.page.slug !== g.pageSlug
	) {
		return '';
	}
	const reviewsBlock = findReviewsBlockOnLandingPage(context.page);
	const fromReviews = reviewsBlock ? mapReviewsBlockToOrganizationInputs(reviewsBlock) : null;

	return jsonLdScriptTag(
		buildOrganizationDeSchema({
			aggregateRating: fromReviews?.aggregateRating ?? null,
			reviews: fromReviews?.reviews?.length ? fromReviews.reviews : null
		})
	);
}
